/**
 * Login Screen - Glassmorphic Redesign
 *
 * Features:
 * - Dark glassmorphic background (#1a1a2e)
 * - GlassCard container with chrome metallic border
 * - GlassButton (theatrical variant) for CTAs
 * - GlassPill brand header
 * - Glassmorphic input fields
 * - Tier-adaptive typography
 * - Full i18n support
 * - WCAG AA accessibility
 *
 * Updated: 2025-02-05 (Glassmorphic Redesign - Phase 1-3)
 */
import { View, Text, TextInput, Alert, Platform, Pressable, ScrollView, StyleSheet } from "react-native";
// Use keyboard-controller's KeyboardAvoidingView for reliable behavior across all devices
import { KeyboardAvoidingView } from "react-native-keyboard-controller";
import { useRouter } from "expo-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTranslation } from "@dramadetector/shared";
import { TierHeadline, TierBody } from "@dramadetector/ui";

import { useAuth } from "../../contexts/AuthContext";
import { loginSchema, type LoginFormData } from "../../lib/auth-validation";
import { useMobileWebKeyboard } from "../../hooks/useMobileWebKeyboard";
import { captureEvent } from "../../lib/analytics";
import { scrubString } from "../../lib/analytics/scrubbers";
import { GoogleSignInButton } from "../../components/GoogleSignInButton";
import { Logo } from "../../components/Logo";
import { GlassButton } from "../../components/glass/GlassButton";
import { GlassPill } from "../../components/glass/GlassPill";
import { debugLog } from "../../lib/debug-logger";

export default function LoginScreen() {
  const { t } = useTranslation('auth');
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);

  // Mobile web keyboard fix: Scroll inputs into view when virtual keyboard appears
  useMobileWebKeyboard();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Bot protection note: Turnstile was removed — token was never validated server-side.
  // If auth abuse becomes a problem, use Supabase Auth's native captcha support:
  // signIn({ captchaToken }) — validates server-side without custom edge functions.
  // See: https://supabase.com/docs/guides/auth/auth-captcha
  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      debugLog('event', 'Attempting login', { email: data.email });
      const { error } = await signIn(data.email, data.password);

      if (error) {
        console.error('[Login] Authentication failed:', error.message);

        // Track login failure (scrub PII - error.message may contain email addresses)
        captureEvent('user_login_failed', {
          error_message: scrubString(error.message),
        });

        Alert.alert(t('login.errors.login_failed'), error.message);
        return;
      }

      debugLog('event', 'Login successful, navigating to tabs');

      // Track login success
      captureEvent('user_login_success', {});

      // Success - AuthContext will handle navigation via session state
      router.replace("/");
    } catch (error) {
      console.error('[Login] Unexpected error:', error);

      // Track unexpected error (scrub PII from error messages)
      captureEvent('user_login_failed', {
        error_message: scrubString(error instanceof Error ? error.message : 'Unknown error'),
        error_type: 'unexpected',
      });

      Alert.alert('Error', t('login.errors.unexpected_error'));
    } finally {
      setLoading(false);
    }
  };

  // Calculate bottom padding for iOS home indicator (no tab bar on auth screens)
  const bottomPadding = Math.max(insets.bottom, 24);

  return (
    <KeyboardAvoidingView
      behavior="padding"
      style={styles.container}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: 24, paddingBottom: bottomPadding }}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.contentContainer}>
          {/* Logo - Brand Consistency */}
          <View style={styles.logoContainer}>
            <Logo size="lg" style={{ marginBottom: 16 }} />
            <GlassPill translationKey="app_name" namespace="common" size="lg" />
          </View>

          {/* Form Content - Direct on dark background (matches home screen) */}
          <View style={styles.formContainer}>
            {/* Header - Tier-Adaptive Typography */}
            <View style={styles.headerContainer}>
              <TierHeadline size="3xl" weight="bold" style={styles.title}>
                {t('login.title')}
              </TierHeadline>
              <TierBody size="lg" style={styles.subtitle}>
                {t('login.subtitle')}
              </TierBody>
            </View>

            {/* Google Sign-in */}
            <GoogleSignInButton disabled={loading} />

            {/* Divider */}
            <View style={styles.dividerContainer}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>{t('login.divider')}</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Email Field */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>
                {t('login.email_label')}
              </Text>
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={[styles.input, errors.email && styles.inputError]}
                    placeholder={t('login.email_placeholder')}
                    placeholderTextColor="rgba(255, 255, 255, 0.4)"
                    autoCapitalize="none"
                    autoCorrect={false}
                    keyboardType="email-address"
                    textContentType="emailAddress"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    editable={!loading}
                    accessibilityLabel={t('login.accessibility.email_address')}
                    accessibilityHint={t('login.accessibility.email_hint')}
                  />
                )}
              />
              {errors.email && (
                <Text style={styles.errorText}>
                  {errors.email.message}
                </Text>
              )}
            </View>

            {/* Password Field */}
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>
                {t('login.password_label')}
              </Text>
              <Controller
                control={control}
                name="password"
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={[styles.input, errors.password && styles.inputError]}
                    placeholder={t('login.password_placeholder')}
                    placeholderTextColor="rgba(255, 255, 255, 0.4)"
                    secureTextEntry
                    textContentType="password"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    editable={!loading}
                    accessibilityLabel={t('login.accessibility.password')}
                    accessibilityHint={t('login.accessibility.password_hint')}
                  />
                )}
              />
              {errors.password && (
                <Text style={styles.errorText}>
                  {errors.password.message}
                </Text>
              )}
            </View>

            {/* Submit Button - Theatrical Variant */}
            <GlassButton
              variant="theatrical"
              size="normal"
              onPress={handleSubmit(onSubmit)}
              disabled={loading}
              style={styles.submitButton}
              showIcon
              testID="sign-in-button"
            >
              {loading ? t('login.signing_in') : t('login.sign_in_button')}
            </GlassButton>

            {/* Signup Link */}
            <View style={styles.linkContainer}>
              <Text style={styles.linkText}>
                {t('login.no_account')}{" "}
              </Text>
              <Pressable
                onPress={() => router.push("/(auth)/signup")}
                accessibilityRole="link"
                accessibilityLabel={t('login.accessibility.sign_up')}
              >
                <Text style={styles.linkTextHighlight}>
                  {t('login.sign_up_link')}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1a1a2e', // Dark glassmorphic background
  },
  contentContainer: {
    width: '100%',
    maxWidth: 480,
    marginHorizontal: 'auto',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  formContainer: {
    // No container styling needed - let form breathe directly on dark background
  },
  headerContainer: {
    marginBottom: 24,
    alignItems: 'center',
  },
  title: {
    textAlign: 'center',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  subtitle: {
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
  dividerText: {
    marginHorizontal: 16,
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.5)',
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 8,
  },
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#FFFFFF',
    // Glassmorphic subtle shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  inputError: {
    borderColor: 'rgba(239, 68, 68, 0.6)', // Red with transparency
  },
  errorText: {
    fontSize: 14,
    color: '#EF4444',
    marginTop: 6,
  },
  submitButton: {
    marginBottom: 16,
  },
  linkContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  linkTextHighlight: {
    fontSize: 14,
    color: '#EC4899', // Pink accent (theatrical gradient primary)
    fontWeight: '600',
  },
});
